<template>
  <div
    class="page-service-category page"
    :class="{ 'empty': !showContentPage && !isLoading }"
  >
    <BreadCrumbs
      v-if="showContentPage"
      class="breadcrumbs"
      :bread-crumbs="breadCrumbsData"
    />
    <section class="service-list-section">
      <SectionTitle
        v-if="showContentPage"
        id="sectionTitle"
        class="service-list-section__title"
        :title="service.parentTypeName"
      />
      <v-tabs
        v-if="highlightCategoryList.length && setIsMobileDone"
        ref="tabsBar"
        :value="activeTab"
        class="fixed-tabs-bar"
        :centered="isMobile"
      >
        <v-tab
          v-for="tab in highlightCategoryList"
          :key="tab.typeId"
          @click="onTabClick(tab)"
        >
          {{ tab.typeName }}
        </v-tab>
      </v-tabs>
      <template v-else-if="showContentPage">
        <div class="service-list-section__tab__loader">
          <v-skeleton-loader
            v-for="x in 4"
            :key="x"
            type="chip"
            height="48"
            class="loader"
          />
        </div>
      </template>
      <transition name="fade">
        <GroupServiceList
          v-if="showContentPage && !isLoading"
          class="service-list-section__list"
          :service-list="service.list"
          :show-highlight-tag="false"
        />
      </transition>
      <ComponentPagination
        v-if="paginationLength > 1"
        class="service-list-pagination"
        :total-page="paginationLength"
        :current-page="activePage"
        @changeActivePageHandler="changeActivePageHandler"
      />
    </section>
    <client-only>
      <ServiceNotAvaliable v-if="!showContentPage && !isLoading" />
    </client-only>
  </div>
</template>

<script>
import ServiceNotAvaliable from '../components/Error/ServiceNotAvaliable.vue'
import SectionTitle from '@/components/ComponentSectionTitle'
import BreadCrumbs from '@/components/ComponentBreadCrumbs.vue'
import GroupServiceList from '@/components/GroupServiceList'
import ComponentPagination from '@/components/ComponentPagination'
import { mapGetters } from 'vuex'
const handleFormatService = service => {
  return {
    serviceId: service.ServiceId,
    typeParentId: service.TypeParentId || null,
    typeId: service.TypeId || null,
    typeName: service.TypeName || null,
    iconName: service.TypeName || null,
    tags: service.Tags || [],
    title: service.Title,
    imgUrl: service.imgUri,
    desc: service.Description,
    hasPromotion: service.HasPromotion,
    serviceAddressMode: service.ServiceAddressMode, // 1:地址模式 2:社區模式
    minimumPriceOrPricingMethod: {
      content: service.MinimumPriceOrPricingMethod.Content,
      isNumber: service.MinimumPriceOrPricingMethod.IsNumber
    },
    ratingSummary: {
      totalRatings: service.RatingSummary.TotalRatings,
      averageRating: service.RatingSummary.AverageRating
    }
  }
}

/**
 * 建構 API 查詢參數
 * @param {object} auth - 認證物件
 * @param {object} store - Vuex store
 * @returns {Promise<object>} 查詢參數
 */
const buildApiQueryParams = async (auth, store) => {
  const postQuery = {
    IsWeb: true
  }

  // 如果使用者已登入，加入地區和社區資訊
  if (auth && auth.loggedIn) {
    try {
      const userDefaultCommunity = await store.dispatch('getUserDefaultCommunity')
      if (userDefaultCommunity.AreaId) {
        postQuery.RegionId = [userDefaultCommunity.AreaId]
        postQuery.DefaultCommunityId = userDefaultCommunity.Id
      } else {
        postQuery.RegionId = []
      }
    } catch (err) {
      console.log('Get user default community error:', err)
    }
  }

  return postQuery
}

/**
 * 根據服務資料建立分類列表
 * @param {Array} serviceList - 服務列表
 * @returns {Array} 分類列表
 */
const buildCategoryList = (serviceList) => {
  if (!Array.isArray(serviceList)) return []

  const categoryMap = {}
  serviceList.forEach(service => {
    const typeId = service.TypeId || service.typeId
    const typeName = service.TypeName || service.typeName
    if (typeId == null) return

    if (!categoryMap[typeId]) {
      categoryMap[typeId] = {
        typeId,
        typeName: typeName || ''
      }
    }
  })

  return Object.values(categoryMap)
}

export default {
  components: {
    ServiceNotAvaliable,
    SectionTitle,
    BreadCrumbs,
    GroupServiceList,
    ComponentPagination
  },
  async asyncData ({ route, env, store, $auth, $axios }) {
    // 在 server 端計算初始 activeTab 並取得精選服務資料
    let initialActiveTab = 0
    let featuredServicesData = null
    const initialTabQuery = route.query && route.query.tab ? route.query.tab : null

    try {
      // 建構 API 查詢參數
      const postQuery = await buildApiQueryParams($auth, store)

      // 取得精選服務資料（只調用一次）
      const { data } = await $axios.get('/api/v2/Main/FeaturedSectionServices', { params: postQuery, useCache: true })
      featuredServicesData = data

      // 如果有 tab 查詢參數，計算對應的 activeTab
      if (initialTabQuery && data && data.Data && data.Data.ServiceList) {
        const categoryList = buildCategoryList(data.Data.ServiceList)
        if (!initialTabQuery || !Array.isArray(categoryList) || categoryList.length === 0) {
          initialActiveTab = 0
        } else {
          const idx = categoryList.findIndex(tab => String(tab.typeId) === String(initialTabQuery))
          initialActiveTab = idx !== -1 ? idx : 0
        }
      }
    } catch (err) {
      console.log('asyncData error:', err)
    }

    return {
      routeFullPath: route.fullPath,
      webBaseUrl: String(env.BASE_WEB_URL || ' ').slice(0, -1),
      activeTab: initialActiveTab,
      cachedFeaturedServicesData: featuredServicesData // 將數據傳遞給 fetch
    }
  },
  data () {
    return {
      breadCrumbsData: [],
      service: {
        parentTypeName: '',
        parentTypeDescription: '',
        list: []
      },
      serviceData: [],
      activePage: 1,
      paginationLength: 1,
      perPageDataCounts: 12,
      activeTab: 0,
      highlightCategoryList: [],
      observer: null,
      setIsMobileDone: false,
      cachedFeaturedServicesData: null // 快取的精選服務資料
    }
  },
  async fetch () {
    try {
      this.breadCrumbsData = [{ label: '精選', routeName: 'highlightSection' }]
      this.service.parentTypeName = '精選'

      let data
      // 優先使用 asyncData 階段快取的資料
      if (this.cachedFeaturedServicesData) {
        data = this.cachedFeaturedServicesData
        // 清空快取，避免記憶體洩漏
        this.cachedFeaturedServicesData = null
      } else {
        // 如果沒有快取資料，才發送 API 請求（通常是客戶端路由切換時）
        const postQuery = await buildApiQueryParams(this.$auth, this.$store)
        const response = await this.$axios.get('/api/v2/Main/FeaturedSectionServices', { params: postQuery, useCache: true })
        data = response.data
      }

      this.serviceData = data.Data.ServiceList.map(handleFormatService)
      this.setHighlightCategoryHandler()

      // 根據 URL 查詢參數設定初始 tab
      if (this.highlightCategoryList.length > 0) {
        this.setTabByQuery(this.$route.query.tab)
      }

      this.service.list = this.activeServiceData.slice((this.activePage - 1) * this.perPageDataCounts, this.perPageDataCounts)
      this.paginationLength = Math.ceil(this.activeServiceData.length / this.perPageDataCounts)
    } catch (err) {
      console.log('err', err)
    }
  },
  head () {
    return {
      title: this.service.parentTypeName,
      meta: [
        { hid: 'title', property: 'title', content: this.service.parentTypeName + this.$store.state.meta.titleTemple },
        { hid: 'og:url', property: 'og:url', content: this.webBaseUrl + this.routeFullPath },
        { hid: 'og:title', property: 'og:title', content: this.service.parentTypeName + this.$store.state.meta.titleTemple },
        this.service.parentTypeDescription ? { hid: 'og:description', property: 'og:description', content: this.service.parentTypeDescription } : {},
        { hid: 'twitter:url', property: 'twitter:url', content: this.webBaseUrl + this.routeFullPath },
        { hid: 'twitter:title', property: 'twitter:title', content: this.service.parentTypeName + this.$store.state.meta.titleTemple },
        this.service.parentTypeDescription ? { hid: 'twitter:description', property: 'twitter:description', content: this.service.parentTypeDescription } : {}
      ]
    }
  },
  computed: {
    ...mapGetters(['isMobile']),
    showContentPage () {
      return this.serviceData.length
    },
    isLoading () {
      return this.$fetchState.pending
    },
    activeTypeId () {
      return this.highlightCategoryList[this.activeTab]?.typeId
    },
    activeServiceData () {
      return this.highlightCategoryList.find(category => category.typeId === this.activeTypeId)?.serviceList || []
    },
    canStartListenToDom () {
      return this.showContentPage && process.client
    }
  },
  watch: {
    '$route.query.tab' (val, oldVal) {
      if (val === oldVal) return
      this.setTabByQuery(val)
      this.$nextTick(() => {
        this.scrollToTabHandler()
      })
    },
    activeTab (val) {
      const category = this.highlightCategoryList[val]
      this.$gaLogEvent && this.$gaLogEvent('分類精選', category?.typeName)
    },
    activeServiceData (val) {
      this.activePage = 1
      this.service.list = val.slice((this.activePage - 1) * this.perPageDataCounts, this.perPageDataCounts)
      this.paginationLength = Math.ceil(val.length / this.perPageDataCounts)
    },
    canStartListenToDom (val) {
      if (val) {
        this.$nextTick(() => {
          this.listenTabSticky()
        })
      }
    }
  },
  mounted () {
    this.$store.commit('setWindowWidth', window.innerWidth)
    this.setIsMobileDone = true
    // 在分類設定完成後，根據 URL 查詢參數設定 tab 並滾動到正確位置
    if (this.highlightCategoryList.length > 0 && this.setIsMobileDone) {
      this.$nextTick(() => {
        this.scrollToTabHandler()
      })
    }
  },
  beforeDestroy () {
    this.removeListenTabSticky()
  },
  methods: {
    /**
     * 處理分頁切換，更新當前頁面數據並滾動至 Tab
     * @param {number} page - 目標頁碼
     */
    async changeActivePageHandler (page) {
      this.scrollToTabHandler(true)
      this.activePage = page
      this.service.list = this.activeServiceData.slice((page - 1) * this.perPageDataCounts, this.perPageDataCounts * page)
    },
    setHighlightCategoryHandler () {
      if (!this.serviceData.length) return
      this.highlightCategoryList = this.serviceData.reduce((accu, curr) => {
        const existCategory = accu.find(v => v.typeId === curr.typeId)
        if (existCategory) {
          existCategory.serviceList.push(curr)
        } else {
          accu.push({
            typeId: curr.typeId,
            typeName: curr.typeName,
            serviceList: [curr]
          })
        }
        return accu
      }, [])
      if (this.activeTab === null) this.activeTab = 0
    },
    removeListenTabSticky () {
      if (!this.showContentPage) return
      const observeElSelector = '#sectionTitle'
      this.observer?.unobserve(document.querySelector(observeElSelector))
    },
    listenTabSticky () {
      if (!this.showContentPage) return
      const observeElSelector = '#sectionTitle'
      this.observer = new IntersectionObserver((entries) => {
        const tabBar = this.$refs.tabsBar?.$el?.querySelector('.v-tabs-bar')
        if (!tabBar) return
        tabBar.classList.toggle('is-fixed', !entries[0].isIntersecting)
      })
      this.observer.observe(document.querySelector(observeElSelector))
    },
    /**
     * 滾動至 Tab 位置，處理移動端和桌面端偏移
     * @param {boolean} isPageChange - 是否為頁面變更
     */
    scrollToTabHandler (isPageChange) {
      if (!process.client) return

      if (!this.$route?.query?.tab && !isPageChange) {
        return
      }

      const tabsClientHeight = this.$refs.tabsBar?.$el?.clientHeight || 48
      const offsetHeight = this.isMobile ? 0 : 20
      this.$vuetify.goTo('.fixed-tabs-bar', {
        duration: 500,
        offset: tabsClientHeight + offsetHeight,
        easing: 'easeInOutQuad'
      }).catch((error) => {
        if (process.env.NODE_ENV === 'development') {
          console.error('Scroll error:', error)
        }
      })
    },
    /**
     * 切換 tab 時，更新網址 query 參數
     * @param {string|number} typeId
     */
    updateTabQuery (typeId) {
      const current = this.$route.query?.tab
      if (String(current) === String(typeId)) return
      const query = { tab: typeId }
      this.$router.replace({ query, params: { noScroll: true, toTop: false } }) // 需清除滾動參數，因為進來時可能帶有滾動參數
    },
    /**
     * 根據 typeId 設定 activeTab
     * @param {string|number} typeId
     */
    setTabByQuery (typeId) {
      if (!Array.isArray(this.highlightCategoryList) || this.highlightCategoryList.length === 0) return
      // 如果沒有提供 typeId，回到第一個 tab
      if (!typeId) {
        if (this.activeTab !== 0) this.activeTab = 0
        return
      }
      const idx = this.highlightCategoryList.findIndex(tab => String(tab.typeId) === String(typeId))
      if (idx !== -1) {
        if (this.activeTab !== idx) this.activeTab = idx
      } else {
        if (this.activeTab !== 0) this.activeTab = 0
      }
    },
    /**
     * 當使用者手動點擊 tab 時觸發
     * @param {object} tab - 被點擊的 tab 物件
     */
    onTabClick (tab) {
      if (tab) {
        this.updateTabQuery(tab.typeId)
      }
    }
  }
}
</script>

<style lang="scss" scoped>
$contentContainerMaxWidth: calc(263px * 4 + 24px * 3);
$contentContainerMaxWidthMobile: calc(263px * 2 + 24px * 1);
@mixin layoutDefined () {
  max-width: calc(#{$contentContainerMaxWidth} + 10vw);
  margin: 0 auto;
  padding: 0 5vw;
  @media screen and (max-width: $mobileWidth) {
    max-width: calc(#{$contentContainerMaxWidthMobile} + 10vw);
  }
}
.page-service-category {
  @include layoutDefined;
  &.empty {
    display: flex;
    justify-content: center;
    align-content: center;
  }
}
.service-list-section {
  position: relative;
  &__title {
    margin-bottom: 32px;
  }
  &__list {
    margin-top: 32px;
  }
  &__tab__loader {
    display: flex;
    .loader {
      +.loader {
        margin-left: 6px;
      }
    }
  }
}
.service-list-pagination {
  width: 100%;
  display: flex;
  justify-content: flex-end;
  margin: 60px 0;
}
::v-deep {
  .fixed-tabs-bar {
    display: initial;
    .v-tabs-slider-wrapper {
      height: 5px !important;
    }
    .v-tabs-bar {
      position: sticky;
      top: $headerHeightMobile;
      z-index: 10;
      &.is-fixed {
        box-shadow: 0px 6px 6px -6px rgba(0, 0, 0, .3)
      }
    }
    .v-slide-group__prev,
    .v-slide-group__next {
      display: none !important;
    }
    /** 手機以上 */
    @media screen and (min-width: $mobileWidth) {
      .v-tabs-bar {
        top: $headerHeight;
      }
    }
  }
  .v-tab {
    font-size: 17px;
    font-weight: bold;
  }
}
@media screen and (max-width: $mobileWidth) {
  .page-service-category {
    &.empty {
      height: calc(100vh - #{$headerHeightMobile});
      padding-bottom: 40px;
    }
  }
  .breadcrumbs {
    margin: 0 auto;
  }
}
</style>
